import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, hoy } from '../utils/formato';
import { saldoDe, abonosDe, proponerDescuentos, cuantasFaltan,
         detalleDelPrestamo } from '../utils/descuento';
import Comprobante from './Comprobante';

const MODALIDADES = {
  jornal: 'Por jornal',
  semanal: 'Semanal',
  quincenal: 'Quincenal',
  mensual: 'Mensual'
};

// La nómina de la finca.
//
// Lo que la hace útil no es calcular un sueldo —eso lo hace cualquiera— sino
// llevar los VALES: los adelantos que pide el trabajador entre quincena y
// quincena. Hasta ahora eso vivía en la memoria de quien paga. Un vale olvidado
// es plata regalada; uno cobrado dos veces es una discusión con el trabajador.
function Nomina() {
  const [gente, setGente] = useState([]);
  const [vales, setVales] = useState([]);
  // A quién se le están mirando los vales. Mirar no puede obligar a abrir la
  // pantalla de pagar: ver el detalle solo se podía entrando por «Liquidar», y
  // de ahí a darle sin querer a «Liquidar y pagar» hay un clic.
  const [viendoVales, setViendoVales] = useState(null);
  // Los abonos de los vales. El saldo NO viene del servidor: se resta aquí con
  // la misma función que usan las pruebas.
  const [abonos, setAbonos] = useState([]);
  const [comprobante, setComprobante] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [nuevo, setNuevo] = useState(null);
  const [dandoVale, setDandoVale] = useState(null);
  const [liquidando, setLiquidando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      axios.get('/api/trabajadores/estado').then(r => setGente(r.data || [])),
      axios.get('/api/vales-trabajadores').then(r => setVales(r.data || [])),
      axios.get('/api/vales-trabajadores/abonos').then(r => setAbonos(r.data || []))
        .catch(() => setAbonos([])),
      axios.get('/api/pagos-trabajadores').then(r => setPagos(r.data || []))
    ]).catch(() => {}).finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  // Vivo = le queda SALDO. Con las cuotas, «sin pago_id» ya no basta.
  const valesDe = (id) => vales.filter(v => v.trabajador_id === id && !v.pago_id
                                            && saldoDe(v, abonos) > 0);

  // Dar de alta y corregir son el MISMO formulario.
  //
  // No se podía corregir nada: ni un nombre mal tecleado —que sale impreso en
  // el comprobante que el trabajador firma—, ni el sueldo cuando se lo suben,
  // ni el cargo. La única salida era borrarlo y crearlo otra vez, y con eso se
  // iban sus vales y su historial de pagos.
  //
  // Dos pantallas para lo mismo serían dos sitios donde cambiar una etiqueta, y
  // el día que cambie una cambiaría en una sola.
  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = { ...nuevo, valor: parseFloat(nuevo.valor) || 0 };
      if (nuevo.id) await axios.put(`/api/trabajadores/${nuevo.id}`, datos);
      else await axios.post('/api/trabajadores', datos);
      setNuevo(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar');
    }
    setGuardando(false);
  };

  // Abrir la ficha de alguien que ya existe.
  //
  // `fecha_ingreso` puede venir nula y el campo de fecha necesita texto: con
  // null React lo trata como no controlado y suelta un aviso en consola, que es
  // de lo que nadie se entera hasta que algo más se rompe al lado.
  const corregir = (t) => setNuevo({
    id: t.id,
    nombre: t.nombre || '', documento: t.documento || '', telefono: t.telefono || '',
    cargo: t.cargo || '', modalidad: t.modalidad || 'quincenal',
    valor: t.valor == null ? '' : String(t.valor),
    fecha_ingreso: (t.fecha_ingreso || '').slice(0, 10),
    activo: t.activo == null ? 1 : t.activo,
    // Para poder avisar antes de darlo de baja con plata pendiente.
    vales_pendientes: t.vales_pendientes || 0,
  });

  const darVale = async (e) => {
    e.preventDefault();
    const monto = parseFloat(dandoVale.monto);
    if (!(monto > 0)) return alert('El monto debe ser mayor que cero');

    setGuardando(true);
    try {
      await axios.post('/api/vales-trabajadores', {
        trabajador_id: dandoVale.trabajador.id,
        fecha: dandoVale.fecha,
        monto,
        cuota: parseFloat(dandoVale.cuota) || null,
        motivo: dandoVale.motivo || null
      });
      setAviso({ tipo: 'bueno',
        texto: `Vale de ${plata(monto)} anotado a ${dandoVale.trabajador.nombre}. Se le descontará en la próxima liquidación.` });
      setDandoVale(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo guardar el vale');
    }
    setGuardando(false);
  };

  // Al abrir la liquidación se propone el bruto según la modalidad y se marcan
  // todos los vales pendientes. Quien paga puede desmarcar los que quiera
  // dejar para la próxima: descontarlos por sorpresa es la clase de cosa que
  // se discute a gritos.
  const abrirLiquidacion = (t) => {
    const pendientes = valesDe(t.id);
    setLiquidando({
      trabajador: t,
      fecha: hoy(),
      jornales: t.modalidad === 'jornal' ? '15' : '',
      bruto: t.modalidad === 'jornal'
        ? String(Math.round((t.valor || 0) * 15))
        : String(Math.round(t.valor || 0)),
      // Marcados con LA CUOTA que toca, no con el vale entero. Descontarle
      // todo de golpe a alguien que pidió tres adelantos lo deja sin quincena.
      // `sinCuotaSeMarca`: un vale SÍ se marca solo aunque no tenga cuota.
      //
      // Es un adelanto contra el sueldo que se está liquidando en esta misma
      // pantalla, y el rótulo de abajo lo dice: «desmarca los que quieras dejar
      // para la próxima». Un préstamo a una finca es otra cosa y allá no se
      // marca solo — ver `proponerDescuentos`.
      marcados: proponerDescuentos(pendientes, abonos,
                                   t.modalidad === 'jornal'
                                     ? (t.valor || 0) * 15 : (t.valor || 0),
                                   { sinCuotaSeMarca: true })
        .filter(x => x.propuesto > 0)
        .map(x => ({ id: x.id, monto: x.propuesto })),
      otros: '',
      nota: ''
    });
    setAviso(null);
  };

  // El bruto se recalcula solo mientras se cambian los jornales, que es lo que
  // varía de una quincena a otra.
  const cambiarJornales = (n) => {
    const t = liquidando.trabajador;
    setLiquidando({
      ...liquidando,
      jornales: n,
      bruto: String(Math.round((parseFloat(n) || 0) * (t.valor || 0)))
    });
  };

  // Cuánto se le descuenta hoy a este vale. Cero = no se descuenta.
  const puestoEn = (id) => {
    const p = (liquidando.marcados || []).find(x => String(x.id) === String(id));
    return p ? Math.max(0, Number(p.monto) || 0) : 0;
  };
  const ponerEnVale = (id, monto) => {
    const otros = (liquidando.marcados || []).filter(x => String(x.id) !== String(id));
    const m = Math.max(0, Math.round(Number(monto) || 0));
    setLiquidando({ ...liquidando,
      marcados: m > 0 ? [...otros, { id, monto: m }] : otros });
  };

  const alternarVale = (id) => {
    if (puestoEn(id) > 0) return ponerEnVale(id, 0);
    const pr = proponerDescuentos(valesDe(liquidando.trabajador.id), abonos,
                                  parseFloat(liquidando.bruto) || 0,
                                  { sinCuotaSeMarca: true })
      .find(x => String(x.id) === String(id));
    // `alMarcar`, no `propuesto`.
    //
    // Al marcar A MANO se pone todo lo que alcance, tenga cuota o no. Con
    // `propuesto`, un vale que no se propuso solo ponía CERO y la casilla no
    // hacía nada: se marcaba y el descuento no aparecía por ningún lado.
    ponerEnVale(id, pr ? pr.alMarcar : saldoDe(
      vales.find(v => String(v.id) === String(id)) || {}, abonos));
  };

  const liquidar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setAviso(null);
    try {
      const { data } = await axios.post('/api/pagos-trabajadores', {
        trabajador_id: liquidando.trabajador.id,
        fecha: liquidando.fecha,
        jornales: liquidando.jornales ? parseFloat(liquidando.jornales) : null,
        bruto: parseFloat(liquidando.bruto),
        // Cuánto de cada vale, no solo cuáles. El servidor lo topa por el
        // saldo de verdad.
        vales: liquidando.marcados,
        otros_descuentos: parseFloat(liquidando.otros) || 0,
        nota: liquidando.nota || null
      });
      setAviso({ tipo: 'bueno',
        texto: `${liquidando.trabajador.nombre}: ${plata(data.bruto)} menos ` +
               `${plata(data.descuentos)} de descuentos = ${plata(data.neto)} a pagar` +
               (data.vales_saldados > 0
                 ? `. Quedaron saldados ${data.vales_saldados} ${data.vales_saldados === 1 ? 'vale' : 'vales'}.`
                 : '.') });
      // El comprobante se arma con lo que devolvió el servidor, no con lo que
      // hay en pantalla: el neto y los vales saldados los calcula él, y el
      // papel tiene que decir lo que quedó guardado.
      const t = liquidando.trabajador;
      const descontados = (liquidando.marcados || [])
        .map(d => {
          const v = vales.find(x => String(x.id) === String(d.id));
          // El renglón dice lo descontado HOY, no el monto del vale — pero el
          // detalle de abajo necesita EL DEL VALE, así que se guarda aparte.
          //
          // Pisando `monto` con lo de hoy, el papel decía «se le prestaron
          // $100.000 — con esto queda a paz y salvo» de un vale de $500.000.
          return v ? { ...v, monto: d.monto, prestado: v.monto } : null;
        })
        .filter(Boolean);
      setComprobante({
        serie: 'NOM',
        numero: data.id,
        fecha: liquidando.fecha,
        aQuien: t.nombre,
        documento: t.documento || null,
        monto: data.neto,
        concepto: liquidando.nota ||
          (liquidando.desde && liquidando.hasta
            ? `Trabajo del ${fecha(liquidando.desde)} al ${fecha(liquidando.hasta)}`
            : 'Semana de trabajo'),
        forma: 'efectivo',
        renglones: [
          { texto: 'Trabajo del periodo', monto: data.bruto },
          // EL PRÉSTAMO, CON LO QUE LLEVA ABONADO Y LO QUE LE QUEDA.
          //
          // El comprobante decía solo «Menos préstamo del 6 de sept:
          // −$100.000». El trabajador NO tiene otra hoja: ve que le
          // descontaron cien mil y para saber cuánto le falta tiene que
          // preguntar. Y es a quien más le importa, que se lo descuentan de su
          // semana y no de una quincena de millones.
          ...descontados.flatMap(v => {
            const d = detalleDelPrestamo({ id: v.id, monto: v.prestado },
                                         abonos, v.monto);
            return [
              { texto: `Menos préstamo del ${fecha(v.fecha)}` +
                       (v.motivo ? ` — ${v.motivo}` : ''),
                monto: v.monto, signo: '-' },
              { chico: true, monto: 0,
                texto: d.saldado
                  ? `Se le prestaron ${plata(d.monto)} — con esto queda a paz y salvo`
                  : `Se le prestaron ${plata(d.monto)} · abonado ${plata(d.abonado)}` +
                    ` — le quedan ${plata(d.queda)}` },
            ];
          }),
          ...(data.descuentos - descontados.reduce((x, v) => x + v.monto, 0) > 0
            ? [{ texto: 'Otros descuentos', signo: '-',
                 monto: data.descuentos - descontados.reduce((x, v) => x + v.monto, 0) }]
            : []),
        ],
      });
      setLiquidando(null);
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo liquidar' });
    }
    setGuardando(false);
  };

  // Lo que se descuenta con lo marcado ahora mismo
  const descuentoActual = liquidando
    ? valesDe(liquidando.trabajador.id)
        .reduce((s2, v) => s2 + puestoEn(v.id), 0) + (parseFloat(liquidando.otros) || 0)
    : 0;
  const netoActual = liquidando
    ? (parseFloat(liquidando.bruto) || 0) - descuentoActual
    : 0;

  const valesTotales = gente.reduce((s, t) => s + (t.vales_pendientes || 0), 0);

  return (
    <>
      {/* Se abre solo al terminar de liquidar. El papel es el último paso del
          pago, no una pantalla aparte a la que haya que acordarse de entrar. */}
      <Comprobante v={comprobante} alCerrar={() => setComprobante(null)} />

      {/* ── LOS VALES DE UNA PERSONA, PARA MIRARLOS ───────────────────────
          
          Con los SALDADOS incluidos. Hasta hoy, un vale que se terminaba de
          pagar desaparecía de la lista y no quedaba dónde consultarlo: el día
          que alguien pregunte «¿y aquel préstamo del año pasado?» no había
          respuesta. Pagado no es lo mismo que borrado. */}
      {viendoVales && (
        <div className="capa">
          <div className="tarjeta">
            <h2>📄 Vales de {viendoVales.nombre}</h2>
            <div className="cuerpo">
              {(() => {
                const suyos = vales
                  .filter(v => String(v.trabajador_id) === String(viendoVales.id))
                  .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));
                if (suyos.length === 0) {
                  return <div className="vacio">
                    A {viendoVales.nombre} no se le ha prestado nada.
                  </div>;
                }
                const debe = suyos.reduce((s, v) => s + saldoDe(v, abonos), 0);
                return (
                  <>
                    <div className="aviso" style={{ marginBottom: 14 }}>
                      {debe > 0
                        ? <>Debe <strong>{plata(debe)}</strong> en{' '}
                           {suyos.filter(v => saldoDe(v, abonos) > 0).length}{' '}
                           {suyos.filter(v => saldoDe(v, abonos) > 0).length === 1
                             ? 'vale' : 'vales'}. Se le descuentan en la próxima quincena.</>
                        : <>Está <strong>a paz y salvo</strong>. No debe ningún vale.</>}
                    </div>
                    {suyos.map(v => {
                      const saldo = saldoDe(v, abonos);
                      const míos = abonosDe(v, abonos);
                      const abonado = Math.round(v.monto - saldo);
                      return (
                        <div key={v.id} style={{ padding: '10px 0',
                                                 borderTop: '1px solid var(--raya)' }}>
                          <div style={{ display: 'flex', gap: 10,
                                        alignItems: 'baseline' }}>
                            <strong>{plata(v.monto)}</strong>
                            <span style={{ fontSize: 13 }}>
                              {fecha(v.fecha)}
                              {v.motivo ? ` · ${v.motivo}` : ''}
                            </span>
                            <span style={{ marginLeft: 'auto' }}>
                              <span className={'pinta ' + (saldo > 0 ? 'ojo' : 'bueno')}>
                                {saldo > 0 ? `le quedan ${plata(saldo)}` : 'a paz y salvo'}
                              </span>
                            </span>
                          </div>
                          {/* Cada abono con su fecha. Un total suelto es la
                              palabra de uno contra la del otro; con las fechas,
                              él lo compara con lo que tiene apuntado. */}
                          {míos.map((a, k) => (
                            <div key={k} style={{ fontSize: 13, paddingLeft: 18,
                                                  color: 'var(--tinta-tenue)',
                                                  display: 'flex', gap: 10 }}>
                              <span>Abonó el {fecha(a.fecha)}</span>
                              <span style={{ marginLeft: 'auto' }}>{plata(a.monto)}</span>
                            </div>
                          ))}
                          {abonado > 0 && (
                            <div style={{ fontSize: 13, paddingLeft: 18, marginTop: 3 }}>
                              Abonado {plata(abonado)} de {plata(v.monto)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              })()}
            </div>
            <div className="acciones">
              <button className="boton suave"
                      onClick={() => setViendoVales(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      <div className="encabezado">
        <h1>Nómina</h1>
        <p>La gente, lo que les prestó y sus quincenas</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--ganado)' }}>
          <div className="rotulo">👷 Trabajadores</div>
          <div className="valor cifra">{gente.length}</div>
          <div className="pie">activos</div>
        </div>
        <div className="dato" style={{ '--color': valesTotales > 0 ? 'var(--ojo)' : 'var(--bueno)' }}>
          <div className="rotulo">📄 Vales sin descontar</div>
          <div className="valor cifra">{plata(valesTotales)}</div>
          <div className="pie">
            {valesTotales > 0 ? 'se descuentan en la próxima quincena' : 'nada pendiente'}
          </div>
        </div>
      </div>

      <div className="bloque">
        <h2>
          👷 La gente
          <span className="contador">{gente.length}</span>
          <button className="boton chico" style={{ marginLeft: 'auto' }}
                  onClick={() => setNuevo({ nombre: '', documento: '', telefono: '',
                                            cargo: '', modalidad: 'quincenal', valor: '',
                                            fecha_ingreso: hoy() })}>
            ➕ Nuevo trabajador
          </button>
        </h2>

        {cargando ? (
          <div className="vacio">Cargando…</div>
        ) : gente.length === 0 ? (
          <div className="vacio">Todavía no hay trabajadores registrados.</div>
        ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Trabajador</th>
                  <th>Cargo</th>
                  <th>Cómo se le paga</th>
                  <th style={{ textAlign: 'right' }}>Valor</th>
                  <th style={{ textAlign: 'right' }}>Vales</th>
                  <th>Último pago</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {gente.map(t => (
                  <tr key={t.id}>
                    <td className="recio">
                      {t.nombre}
                      {t.telefono && (
                        <div style={{ fontSize: 12, color: 'var(--tinta-tenue)', fontWeight: 400 }}>
                          {t.telefono}
                        </div>
                      )}
                    </td>
                    <td>{t.cargo || '—'}</td>
                    <td><span className="pinta gris">{MODALIDADES[t.modalidad] || t.modalidad}</span></td>
                    <td className="num">
                      {plata(t.valor)}
                      <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                        {t.modalidad === 'jornal' ? 'por jornal' : t.modalidad}
                      </div>
                    </td>
                    <td className="num" style={{ color: t.vales_pendientes > 0 ? 'var(--ojo)' : 'var(--tinta-tenue)' }}>
                      {t.vales_pendientes > 0
                        ? <>
                            {plata(t.vales_pendientes)}
                            <div style={{ fontSize: 11 }}>
                              {t.cuantos_vales} {t.cuantos_vales === 1 ? 'vale' : 'vales'}
                            </div>
                          </>
                        : '—'}
                    </td>
                    <td>
                      {t.ultimo_pago
                        ? <>{fecha(t.ultimo_pago)}
                            <div style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                              {plata(t.ultimo_neto)}
                            </div>
                          </>
                        : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {/* VER LOS VALES SIN TOCAR NADA.
                          
                          Para saber de cuándo era un vale, por qué se le prestó
                          y cuánto lleva abonado, había que darle a «Liquidar» y
                          después a Cancelar. Entrar por la puerta de pagar para
                          mirar es como se hace un pago que nadie quería. */}
                      {/* Corregir la ficha: el nombre mal tecleado, el sueldo
                          que le subieron, el cargo. Antes no había forma, y la
                          única salida era borrarlo y crearlo de nuevo — con lo
                          que se perdían sus vales y sus pagos. */}
                      <button className="boton chico suave"
                              style={{ marginRight: 5 }}
                              title="Corregir nombre, cargo o sueldo"
                              onClick={() => corregir(t)}>
                        ✏️
                      </button>
                      <button className="boton chico suave"
                              style={{ marginRight: 5 }}
                              onClick={() => setViendoVales(t)}>
                        📄 Vales
                      </button>
                      <button className="boton chico suave" style={{ marginRight: 6 }}
                              onClick={() => setDandoVale({ trabajador: t, fecha: hoy(),
                                                            monto: '', cuota: '', motivo: '' })}>
                        {/* Dice «Prestarle» y no «Vale».
                            «Vale» es como se llama el papel en una finca, y es
                            el término correcto — pero nadie lo busca así.
                            Carlos preguntó dónde se anota un préstamo a un
                            trabajador teniendo el botón al frente. En Fincas el
                            mismo botón ya decía «Prestarle»: eran dos palabras
                            para lo mismo dentro del mismo programa.

                            La palabra «vale» se queda en el cuadro que se abre y
                            en la columna, que es donde explica el concepto. */}
                        Prestarle
                      </button>
                      <button className="boton chico" onClick={() => abrirLiquidacion(t)}>
                        Liquidar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dar de alta o corregir: el mismo formulario */}
      {nuevo && (
        <div className="bloque">
          <h2>{nuevo.id ? `✏️ ${nuevo.nombre || 'Corregir trabajador'}` : '➕ Nuevo trabajador'}</h2>
          <form className="cuerpo" onSubmit={guardar}>
            <div className="campos">
              <label className="campo">
                <span>Nombre *</span>
                <input value={nuevo.nombre} required
                       onChange={e => setNuevo({ ...nuevo, nombre: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cédula</span>
                <input value={nuevo.documento}
                       onChange={e => setNuevo({ ...nuevo, documento: e.target.value })} />
              </label>
              <label className="campo">
                <span>Teléfono</span>
                <input value={nuevo.telefono}
                       onChange={e => setNuevo({ ...nuevo, telefono: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cargo</span>
                <input value={nuevo.cargo} placeholder="Quesero, ordeñador…"
                       onChange={e => setNuevo({ ...nuevo, cargo: e.target.value })} />
              </label>
              <label className="campo">
                <span>Cómo se le paga</span>
                <select value={nuevo.modalidad}
                        onChange={e => setNuevo({ ...nuevo, modalidad: e.target.value })}>
                  {/* De lo más corto a lo más largo. El trabajador de finca
                      que cobra el sábado es de lo más común y no había cómo
                      anotarlo: quedaba como quincenal, que es otra cosa. */}
                  <option value="jornal">Por jornal</option>
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                  <option value="mensual">Mensual</option>
                </select>
              </label>
              <label className="campo">
                <span>
                  {{ jornal: 'Valor del jornal *', semanal: 'Valor de la semana *',
                     quincenal: 'Valor de la quincena *', mensual: 'Valor del mes *'
                   }[nuevo.modalidad] || 'Valor del periodo *'}
                </span>
                <input type="number" step="1" className="peso" required value={nuevo.valor}
                       onChange={e => setNuevo({ ...nuevo, valor: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha de ingreso</span>
                <input type="date" value={nuevo.fecha_ingreso}
                       onChange={e => setNuevo({ ...nuevo, fecha_ingreso: e.target.value })} />
              </label>
            </div>
            {nuevo.id && (
              <>
                {/* CAMBIAR EL SUELDO NO RETOCA LO YA PAGADO.

                    Es la pregunta que llega después: «le subí el sueldo, ¿y las
                    quincenas que ya le pagué?». Lo liquidado quedó como quedó —
                    esa plata ya cambió de manos— y recalcularlo hacia atrás
                    cambiaría comprobantes que el trabajador ya firmó. */}
                <div className="aviso" style={{ marginTop: 4 }}>
                  Lo que cambie aquí rige <strong>de la próxima liquidación en
                  adelante</strong>. Los pagos ya hechos se quedan como están.
                </div>

                {/* DAR DE BAJA, NO BORRAR.

                    Borrar a alguien que tiene pagos y vales anotados sería
                    perder la prueba de lo que ya se le pagó. Se marca como que
                    ya no trabaja: sale de la lista y su historia se queda. */}
                {/* Sin `className="campo"`: esa clase estira el renglón a todo
                    lo ancho y dejaba la casilla a un lado de la pantalla y su
                    texto al otro, como si no fueran la misma cosa. */}
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
                                marginTop: 12, cursor: 'pointer', fontSize: 14 }}>
                  <input type="checkbox" checked={Number(nuevo.activo) === 0}
                         onChange={e => setNuevo({ ...nuevo, activo: e.target.checked ? 0 : 1 })} />
                  Ya no trabaja aquí
                </label>
                {Number(nuevo.activo) === 0 && (
                  <div className={'aviso ' + (nuevo.vales_pendientes > 0 ? 'malo' : '')}
                       style={{ marginTop: 6 }}>
                    {nuevo.vales_pendientes > 0
                      ? <>Le quedan <strong>{plata(nuevo.vales_pendientes)}</strong> en vales
                          sin descontar. Sale de la lista y esa plata deja de
                          cobrarse sola: cóbrela en la liquidación final antes de
                          darlo de baja.</>
                      : <>Sale de la lista de la nómina. <strong>No se borra
                          nada</strong>: sus pagos y sus vales se quedan.</>}
                  </div>
                )}
              </>
            )}

            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setNuevo(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Dar un vale */}
      {dandoVale && (
        <div className="bloque">
          <h2>💵 Prestarle a {dandoVale.trabajador.nombre}</h2>
          <form className="cuerpo" onSubmit={darVale}>
            <div className="aviso">
              Un vale es un adelanto. Queda pendiente y se le descuenta cuando se
              liquide la quincena.
              {dandoVale.trabajador.vales_pendientes > 0 && (
                <> Ya tiene <strong>{plata(dandoVale.trabajador.vales_pendientes)}</strong> sin descontar.</>
              )}
            </div>
            <div className="campos">
              <label className="campo">
                <span>Monto *</span>
                <input type="number" step="1" className="peso" required autoFocus
                       value={dandoVale.monto}
                       onChange={e => setDandoVale({ ...dandoVale, monto: e.target.value })} />
              </label>
              {/* LA CUOTA. Sin esto el vale se descontaba entero en la
                  quincena siguiente, y a quien pidió un adelanto grande eso lo
                  deja sin quincena. */}
              <label className="campo">
                <span>¿Cuánto le descuenta cada quincena?</span>
                <input type="number" step="1" min="0" className="peso"
                       value={dandoVale.cuota}
                       placeholder="Vacío = todo de una vez"
                       onChange={e => setDandoVale({ ...dandoVale, cuota: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={dandoVale.fecha}
                       onChange={e => setDandoVale({ ...dandoVale, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Para qué</span>
                <input value={dandoVale.motivo} placeholder="Opcional"
                       onChange={e => setDandoVale({ ...dandoVale, motivo: e.target.value })} />
              </label>
              {/* El plazo, mientras se teclea: es lo que pregunta el
                  trabajador al recibir la plata. */}
              {(() => {
                const m = parseFloat(dandoVale.monto) || 0;
                const c = parseFloat(dandoVale.cuota) || 0;
                if (!(m > 0 && c > 0)) return null;
                const n = cuantasFaltan({ monto: m, cuota: c }, []);
                return (
                  <div className="aviso" style={{ gridColumn: '1 / -1' }}>
                    Se le descuentan <strong>{plata(c)}</strong> por liquidación:{' '}
                    <strong>{n} {n === 1 ? 'vez' : 'veces'}</strong> para quedar en cero.
                    {m % c !== 0 && <> La última sería de {plata(m - c * (n - 1))}.</>}
                  </div>
                );
              })()}
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setDandoVale(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando}>
                {guardando ? 'Guardando…' : '💾 Anotar el vale'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liquidar la quincena */}
      {liquidando && (
        <div className="bloque">
          <h2>💵 Liquidar a {liquidando.trabajador.nombre}</h2>
          <form className="cuerpo" onSubmit={liquidar}>
            <div className="campos">
              {liquidando.trabajador.modalidad === 'jornal' && (
                <label className="campo">
                  <span>Jornales trabajados *</span>
                  <input type="number" step="0.5" min="0" className="peso" required
                         value={liquidando.jornales}
                         onChange={e => cambiarJornales(e.target.value)} />
                </label>
              )}
              <label className="campo">
                <span>Bruto *</span>
                <input type="number" step="1" min="0" className="peso" required
                       value={liquidando.bruto}
                       onChange={e => setLiquidando({ ...liquidando, bruto: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={liquidando.fecha}
                       onChange={e => setLiquidando({ ...liquidando, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Otros descuentos</span>
                <input type="number" step="1" min="0" className="peso" value={liquidando.otros}
                       placeholder="0"
                       onChange={e => setLiquidando({ ...liquidando, otros: e.target.value })} />
              </label>
            </div>

            {/* Los vales, uno por uno y con casilla. Se pueden dejar para la
                próxima: descontarle todo de golpe a alguien que pidió tres
                adelantos puede dejarlo sin quincena. */}
            {valesDe(liquidando.trabajador.id).length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tinta-media)',
                              marginBottom: 8 }}>
                  VALES PENDIENTES — desmarca los que quieras dejar para la próxima
                </div>
                {valesDe(liquidando.trabajador.id).map(v => {
                  const saldo = saldoDe(v, abonos);
                  const yaAbonado = abonosDe(v, abonos);
                  const puesto = puestoEn(v.id);
                  return (
                  <label key={v.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                    border: '1px solid var(--raya)', borderRadius: 8, marginBottom: 6,
                    background: puesto > 0 ? 'var(--ojo-piso)' : 'var(--tarjeta)',
                    cursor: 'pointer', flexWrap: 'wrap'
                  }}>
                    <input type="checkbox" style={{ width: 'auto' }}
                           checked={puesto > 0}
                           onChange={() => alternarVale(v.id)} />
                    {/* EL SALDO, no el monto: con las cuotas hay vales a
                        medias, y enseñar el monto haría creer que se le va a
                        descontar lo que ya abonó. */}
                    <span className="cifra" style={{ fontWeight: 700, minWidth: 100 }}>
                      {plata(saldo)}
                    </span>
                    {yaAbonado.length > 0 && (
                      <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                        de {plata(v.monto)} · {yaAbonado.length}{' '}
                        {yaAbonado.length === 1 ? 'abono' : 'abonos'}
                      </span>
                    )}
                    {/* Cuánto se le descuenta HOY. El programa propone la
                        cuota; quien liquida decide. */}
                    {puesto > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>hoy</span>
                        <input type="number" step="1" min="0" max={saldo}
                               className="peso" style={{ width: 120 }}
                               value={puesto}
                               onClick={e => e.preventDefault()}
                               onChange={e => ponerEnVale(v.id, e.target.value)} />
                      </span>
                    )}
                    {v.cuota > 0 && (
                      <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                        cuota {plata(v.cuota)}
                      </span>
                    )}
                    <span style={{ color: 'var(--tinta-tenue)', fontSize: 13 }}>
                      {fecha(v.fecha)}{v.motivo ? ` · ${v.motivo}` : ''}
                    </span>
                  </label>
                  );
                })}
              </div>
            )}

            <div className="aviso" style={{ marginTop: 18, marginBottom: 0,
                                            background: 'var(--hondo)' }}>
              <div className="tabla-caja">
                <table className="datos" style={{ background: 'transparent' }}>
                  <tbody>
                    <tr>
                      <td>Bruto</td>
                      <td className="num">{plata(parseFloat(liquidando.bruto) || 0)}</td>
                    </tr>
                    <tr>
                      <td>Descuentos</td>
                      <td className="num" style={{ color: 'var(--malo)' }}>
                        − {plata(descuentoActual)}
                      </td>
                    </tr>
                    <tr>
                      <td className="recio">A pagar</td>
                      <td className="num recio" style={{
                        fontSize: 18,
                        color: netoActual < 0 ? 'var(--malo)' : 'var(--bueno)'
                      }}>
                        {plata(netoActual)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {netoActual < 0 && (
                <div style={{ color: 'var(--malo)', marginTop: 8, fontWeight: 600 }}>
                  Los descuentos se pasan del bruto. Desmarca algún vale y déjalo
                  para la próxima.
                </div>
              )}
            </div>

            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setLiquidando(null)}>
                Cancelar
              </button>
              <button className="boton" disabled={guardando || netoActual < 0}>
                {guardando ? 'Guardando…' : '💵 Liquidar y pagar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {pagos.length > 0 && (
        <div className="bloque">
          <h2>📋 Pagos hechos <span className="contador">{pagos.length}</span></h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Trabajador</th>
                  <th style={{ textAlign: 'right' }}>Jornales</th>
                  <th style={{ textAlign: 'right' }}>Bruto</th>
                  <th style={{ textAlign: 'right' }}>Descuentos</th>
                  <th style={{ textAlign: 'right' }}>Pagado</th>
                </tr>
              </thead>
              <tbody>
                {pagos.slice(0, 40).map(p => (
                  <tr key={p.id}>
                    <td>{fecha(p.fecha)}</td>
                    <td className="recio">{p.trabajador}</td>
                    <td className="num">{p.jornales || '—'}</td>
                    <td className="num">{plata(p.bruto)}</td>
                    <td className="num" style={{ color: p.descuentos > 0 ? 'var(--malo)' : undefined }}>
                      {p.descuentos > 0 ? '− ' + plata(p.descuentos) : '—'}
                    </td>
                    <td className="num recio">{plata(p.neto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

export default Nomina;
